import { trpc } from "@/api/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/shared/lib/utils";
import { Link, useRouter } from "@tanstack/react-router";
import type { NotificationType } from "@template/shared";
import { AlertTriangle, Bell, CheckCircle2, Info, Settings } from "lucide-react";
import { toast } from "sonner";

const UNREAD_POLL_INTERVAL_MS = 30_000;
const BELL_PREVIEW_LIMIT = 10;

const TYPE_STYLES: Record<NotificationType, { Icon: typeof Info; colorClass: string }> = {
  info: { Icon: Info, colorClass: "text-blue-500" },
  success: { Icon: CheckCircle2, colorClass: "text-primary" },
  warning: { Icon: AlertTriangle, colorClass: "text-amber-500" },
  system: { Icon: Settings, colorClass: "text-muted-foreground" },
};

/**
 * Bell dropdown that polls unread count every 30 seconds and shows the last
 * 10 notifications with a "Mark all as read" action.
 */
export function NotificationBell() {
  const utils = trpc.useUtils();
  const router = useRouter();

  const unreadCountQuery = trpc.notifications.unreadCount.useQuery(undefined, {
    refetchInterval: UNREAD_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });
  const unreadCount = unreadCountQuery.data?.count ?? 0;

  const recentQuery = trpc.notifications.list.useQuery({
    page: 1,
    pageSize: BELL_PREVIEW_LIMIT,
    unreadOnly: false,
  });
  const recent = recentQuery.data?.items ?? [];

  const markAllReadMutation = trpc.notifications.markAllRead.useMutation({
    onSuccess: (data) => {
      if (data.count > 0) {
        toast.success(`Označeno ${data.count} oznámení jako přečtená`);
      }
      utils.notifications.list.invalidate();
      utils.notifications.unreadCount.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const markReadMutation = trpc.notifications.markRead.useMutation({
    onSuccess: () => {
      utils.notifications.list.invalidate();
      utils.notifications.unreadCount.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative gap-2"
          aria-label={`Oznámení (${unreadCount} nepřečtených)`}
          data-testid="notifications-bell-button"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 ? (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 h-5 min-w-5 px-1 text-xs"
              data-testid="notifications-bell-badge"
            >
              {badgeLabel}
            </Badge>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notifications-bell-dropdown">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Oznámení</span>
          {unreadCount > 0 ? (
            <button
              type="button"
              className="text-xs font-normal text-primary hover:underline"
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              data-testid="notifications-bell-mark-all-read"
            >
              Označit vše jako přečtené
            </button>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {recentQuery.isPending ? (
          <div
            className="px-2 py-6 text-center text-sm text-muted-foreground"
            data-testid="notifications-bell-loading"
          >
            Načítám…
          </div>
        ) : recent.length === 0 ? (
          <div
            className="px-2 py-6 text-center text-sm text-muted-foreground"
            data-testid="notifications-bell-empty"
          >
            Zatím žádná oznámení.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {recent.map((notification) => {
              const { Icon, colorClass } = TYPE_STYLES[notification.type];
              const isUnread = notification.readAt === null;
              const formattedDate = new Date(notification.createdAt).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });

              return (
                <DropdownMenuItem
                  key={notification.id}
                  className={cn(
                    "flex items-start gap-2 p-3 focus:bg-muted",
                    notification.linkUrl && "cursor-pointer",
                  )}
                  onSelect={(event) => {
                    if (isUnread) {
                      markReadMutation.mutate({ id: notification.id });
                    }
                    // S odkazem navigujeme (menu se zavře); bez odkazu jen
                    // označíme přečtené a menu necháme otevřené.
                    if (notification.linkUrl) {
                      router.history.push(notification.linkUrl);
                    } else {
                      event.preventDefault();
                    }
                  }}
                  data-testid={`notifications-bell-item-${notification.id}`}
                >
                  <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", colorClass)} />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={cn(
                          "truncate text-sm",
                          isUnread ? "font-semibold" : "font-normal",
                        )}
                      >
                        {notification.title}
                      </span>
                      {isUnread ? (
                        <span
                          className="h-2 w-2 shrink-0 rounded-full bg-primary"
                          aria-label="Nepřečtené"
                        />
                      ) : null}
                    </div>
                    {notification.message ? (
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {notification.message}
                      </p>
                    ) : null}
                    <span className="text-xs text-muted-foreground">{formattedDate}</span>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to="/dashboard/notifications"
            className="w-full cursor-pointer text-center text-sm"
            data-testid="notifications-bell-see-all"
          >
            Zobrazit všechna oznámení
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
